import { createSlice, createSelector, PayloadAction } from "@reduxjs/toolkit";
import { MedicationModel } from "app/services/api/types/medications/models/MedicationModel";
import { MedicationReportModel } from "app/services/api/types/medications/models/MedicationReportModel";
import { MedicationIndexResponse } from "app/services/api/types/medications/responses/MedicationIndexResponse";
import { MedicationReportResponse } from "app/services/api/types/medications/responses/MedicationReportResponse";
import { RootState } from "app/services/store";

type ResidentId = number;

interface State {
  logs: Record<ResidentId, MedicationReportModel[]>;
  activeMedications: Record<ResidentId, MedicationModel[]>;
  discontinuedMedications: Record<ResidentId, MedicationModel[]>;
  pendingSignatureMedications: Record<ResidentId, MedicationModel[]>;
}
const initialState: State = {
  logs: {},
  activeMedications: {},
  discontinuedMedications: {},
  pendingSignatureMedications: {},
};

export const medicationsSlice = createSlice({
  name: "medications",
  initialState,
  reducers: {
    storeMedicationLogs: (
      state,
      action: PayloadAction<MedicationReportResponse>,
    ) => {
      const { data } = action.payload;
      data.forEach((entry) => {
        const residentId = entry.resident.id;
        if (!state.logs[residentId]) {
          state.logs[residentId] = [];
        }

        // Check if the entry already exists
        const exists = state.logs[residentId].some(
          (existingEntry) => existingEntry.id === entry.id,
        );
        if (!exists) {
          state.logs[residentId].push(entry);
        }
      });
    },
    storeActiveMedications: (
      state,
      action: PayloadAction<MedicationIndexResponse>,
    ) => {
      const data = action.payload;
      const entries = Array.isArray(data) ? data : data.data;
      entries.forEach((entry) => {
        const residentId = entry.resident_id;
        if (!state.activeMedications[residentId]) {
          state.activeMedications[residentId] = [];
        }

        // Check if the entry already exists
        const exists = state.activeMedications[residentId].some(
          (existingEntry) => existingEntry.id === entry.id,
        );
        if (!exists) {
          state.activeMedications[residentId].push(entry);
        }
      });
    },
    storeDiscontinuedMedications: (
      state,
      action: PayloadAction<MedicationIndexResponse>,
    ) => {
      const data = action.payload;
      const entries = Array.isArray(data) ? data : data.data;
      entries.forEach((entry) => {
        const residentId = entry.resident_id;
        if (!state.discontinuedMedications) {
          state.discontinuedMedications = {};
        }
        if (!state.discontinuedMedications[residentId]) {
          state.discontinuedMedications[residentId] = [];
        }

        // Check if the entry already exists
        const exists = state.discontinuedMedications[residentId].some(
          (existingEntry) => existingEntry.id === entry.id,
        );
        if (!exists) {
          state.discontinuedMedications[residentId].push(entry);
        }
      });
    },
    storePendingSignaturesMedications: (
      state,
      action: PayloadAction<MedicationIndexResponse>,
    ) => {
      const data = action.payload;
      const entries = Array.isArray(data) ? data : data.data;
      entries.forEach((entry) => {
        const residentId = entry.resident_id;
        if (!state.pendingSignatureMedications) {
          state.pendingSignatureMedications = {};
        }
        if (!state.pendingSignatureMedications[residentId]) {
          state.pendingSignatureMedications[residentId] = [];
        }

        // Check if the entry already exists
        const exists = state.pendingSignatureMedications[residentId].some(
          (existingEntry) => existingEntry.id === entry.id,
        );
        if (!exists) {
          state.pendingSignatureMedications[residentId].push(entry);
        }
      });
    },
  },
});

export const {
  storeMedicationLogs,
  storeActiveMedications,
  storeDiscontinuedMedications,
  storePendingSignaturesMedications,
} = medicationsSlice.actions;

// Create a base selector for getting resident entries
export const selectResidentMedicationLogs = createSelector(
  [
    (state: RootState) => state.medications?.logs,
    (_: RootState, residentId?: number) => residentId,
  ],
  (logs, residentId) => (!residentId ? [] : logs?.[residentId] || []),
);

export const selectResidentMedicationLogsById = createSelector(
  [
    selectResidentMedicationLogs,
    (_: RootState, _residentId: number | undefined, medicationId?: number) =>
      medicationId,
  ],
  (logs, medicationId) => {
    if (!medicationId) return logs;
    return logs
      .filter((log) => log.medication.id === medicationId)
      .sort((a, b) => b.id - a.id);
  },
);

export const selectResidentActiveMedications = createSelector(
  [
    (state: RootState) => state.medications?.activeMedications,
    (_: RootState, residentId?: number) => residentId,
  ],
  (medications, residentId) =>
    !residentId ? [] : medications?.[residentId] || [],
);

export const selectResidentActiveMedicationById = createSelector(
  [
    selectResidentActiveMedications,
    (_: RootState, _residentId: number | undefined, medicationId?: number) =>
      medicationId,
  ],
  (medications, medicationId) => {
    if (!medicationId) return medications;
    return medications.find((medication) => medication.id === medicationId);
  },
);

export const selectResidentDiscontinuedMedications = createSelector(
  [
    (state: RootState) => state.medications?.discontinuedMedications,
    (_: RootState, residentId?: number) => residentId,
  ],
  (medications, residentId) =>
    !residentId ? [] : medications?.[residentId] || [],
);

export const selectResidentDiscontinuedMedicationById = createSelector(
  [
    selectResidentDiscontinuedMedications,
    (_: RootState, _residentId: number | undefined, medicationId?: number) =>
      medicationId,
  ],
  (medications, medicationId) => {
    if (!medicationId) return medications;
    return medications.find((medication) => medication.id === medicationId);
  },
);

export const selectResidentPendingSignatureMedications = createSelector(
  [
    (state: RootState) => state.medications?.pendingSignatureMedications,
    (_: RootState, residentId?: number) => residentId,
  ],
  (medications, residentId) =>
    !residentId ? [] : medications?.[residentId] || [],
);

export const selectResidentPendingSignatureMedicationsById = createSelector(
  [
    selectResidentPendingSignatureMedications,
    (_: RootState, _residentId: number | undefined, medicationId?: number) =>
      medicationId,
  ],
  (medications, medicationId) => {
    if (!medicationId) return medications;
    return medications.find((medication) => medication.id === medicationId);
  },
);

export default medicationsSlice.reducer;
